import { useState, useEffect, useCallback } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import "./PanelSelector.scss";
import {
    IconObjectAlongPath,
    IconObjectFillPack,
    IconPrimitivePoints,
    IconArcConnections,
    IconRectNodeConnections,
    IconTextNoiseFill,
    IconPointDistortion,
    IconGridEditor,
    IconTraitConnections,
    IconSvgToRaster,
    IconAsciiArt,
    IconLuminanceGrid,
    IconFontMixer,
    IconBlobField,
    IconFlowField,
    IconDomainWarp,
    IconDisplacementMap,
    IconSuperformula,
    IconTopographicMap,
    IconNestedGrid,
    IconPolarGridCells,
    IconVoronoi,
    IconPathAgents,
    IconFlowAgents,
    IconChannelSplit,
    IconDistortionGrid,
    IconLatticeDeform,
    IconPixelStretch,
    IconSymbolGenerator,
    IconPixelSvgCanvas,
    IconArtbrushSvgCanvas,
    IconGridCellWrapper,
    IconNesGlitch,
    IconGridCubes3d,
    IconSilhouetteExtractor,
    IconVolumeBuilder3d,
    IconSvgDiagram2D,
} from "./PanelIcons";
import type { ComponentType, CSSProperties, SVGProps } from "react";

export type PanelType =
    | "object-along-path"
    | "object-fill-pack"
    | "primitive-points"
    | "arc-connections"
    | "rect-node-connections"
    | "text-noise-fill"
    | "point-distortion"
    | "grid-editor"
    | "trait-connections"
    | "svg-to-raster"
    | "ascii-art"
    | "luminance-grid"
    | "font-mixer"
    | "blob-field"
    | "flow-field"
    | "domain-warp"
    | "displacement-map"
    | "superformula"
    | "topographic-map"
    | "nested-grid"
    | "voronoi-diagram"
    | "path-agents"
    | "flow-agents"
    | "channel-split"
    | "distortion-grid"
    | "symbol-generator"
    | "pixel-svg-canvas"
    | "artbrush-svg-canvas"
    | "grid-cell-wrapper"
    | "polar-grid-cells"
    | "nes-glitch"
    | "lattice-deform"
    | "pixel-stretch"
    | "grid-cubes-3d"
    | "silhouette-extractor"
    | "volume-builder-3d"
    | "svg-diagram-2d";

/**
 * Publication state of a tool.
 * - "published" (default): visible everywhere.
 * - "draft": hidden from the nav and not deep-linkable in production builds
 *   (Vercel), but fully available in local `npm run dev`. Use this to ship
 *   code to GitHub without surfacing an unfinished tool on the live site.
 */
type PanelState = "published" | "draft";

/** Drafts are shown in dev, hidden in production builds. */
const SHOW_DRAFTS = import.meta.env.DEV;

interface PanelConfig {
    id: PanelType;
    label: string;
    /** Toolbar icon tint (stroke/fill use currentColor) */
    accent: string;
    Icon: ComponentType<SVGProps<SVGSVGElement>>;
    /** Defaults to "published" when omitted. */
    state?: PanelState;
}

function isPanelVisible(tool: PanelConfig): boolean {
    return SHOW_DRAFTS || tool.state !== "draft";
}

interface PanelGroup {
    id: string;
    label: string;
    tools: PanelConfig[];
}

const PANEL_GROUPS: PanelGroup[] = [
    {
        id: "distribute-fill",
        label: "Distribute & Fill",
        tools: [
            {
                id: "object-along-path",
                label: "Object Along Path",
                accent: "#e07a5f",
                Icon: IconObjectAlongPath,
            },
            {
                id: "object-fill-pack",
                label: "Object Fill Pack",
                accent: "#d4a574",
                Icon: IconObjectFillPack,
            },
            {
                id: "primitive-points",
                label: "Primitive Points",
                accent: "#7ca7ff",
                Icon: IconPrimitivePoints,
            },
        ],
    },
    {
        id: "typography",
        label: "Typography",
        tools: [
            {
                id: "text-noise-fill",
                label: "Text Noise Fill",
                accent: "#7e9cb8",
                Icon: IconTextNoiseFill,
            },
            { id: "font-mixer", label: "Font Mixer", accent: "#9b8cff", Icon: IconFontMixer },
        ],
    },
    {
        id: "image-based",
        label: "Image-based",
        tools: [
            { id: "ascii-art", label: "ASCII Art", accent: "#7cb87c", Icon: IconAsciiArt },
            {
                id: "luminance-grid",
                label: "Luminance Grid",
                accent: "#b0b8c4",
                Icon: IconLuminanceGrid,
            },
            {
                id: "channel-split",
                label: "Channel Split",
                accent: "#e91e63",
                Icon: IconChannelSplit,
            },
            {
                id: "pixel-svg-canvas",
                label: "Pixel SVG Canvas",
                accent: "#ff6f9c",
                Icon: IconPixelSvgCanvas,
            },
            {
                id: "artbrush-svg-canvas",
                label: "Artbrush SVG Canvas",
                accent: "#c77dff",
                Icon: IconArtbrushSvgCanvas,
            },
            {
                id: "grid-cell-wrapper",
                label: "Grid Cell Wrapper",
                accent: "#5c8dff",
                Icon: IconGridCellWrapper,
            },
            {
                id: "nes-glitch",
                label: "NES Glitch",
                accent: "#a06bff",
                Icon: IconNesGlitch,
            },
            {
                id: "pixel-stretch",
                label: "Pixel Stretch",
                accent: "#ff9f43",
                Icon: IconPixelStretch,
            },
        ],
    },
    {
        id: "fields-generative",
        label: "Fields & Generative",
        tools: [
            { id: "blob-field", label: "Blob Field", accent: "#52c9a0", Icon: IconBlobField },
            { id: "flow-field", label: "Flow Field", accent: "#4dd4e8", Icon: IconFlowField },
            { id: "domain-warp", label: "Domain Warp", accent: "#b388ff", Icon: IconDomainWarp },
            {
                id: "topographic-map",
                label: "Topographic Map",
                accent: "#8bc34a",
                Icon: IconTopographicMap,
            },
            {
                id: "superformula",
                label: "Superformula",
                accent: "#f06292",
                Icon: IconSuperformula,
            },
            {
                id: "symbol-generator",
                label: "Symbol Generator",
                accent: "#8d6e63",
                Icon: IconSymbolGenerator,
            },
        ],
    },
    {
        id: "agents",
        label: "Agents",
        tools: [
            { id: "flow-agents", label: "Flow Agents", accent: "#3dd6c0", Icon: IconFlowAgents },
            { id: "path-agents", label: "Path Agents", accent: "#f4a460", Icon: IconPathAgents },
        ],
    },
    {
        id: "distortion",
        label: "Distortion",
        tools: [
            {
                id: "lattice-deform",
                label: "Lattice Deform",
                accent: "#3b82f6",
                Icon: IconLatticeDeform,
            },
            {
                id: "point-distortion",
                label: "Point Distortion",
                accent: "#c77dff",
                Icon: IconPointDistortion,
            },
            {
                id: "displacement-map",
                label: "Displacement Map",
                accent: "#e9c46a",
                Icon: IconDisplacementMap,
            },
            {
                id: "distortion-grid",
                label: "Distortion Grid",
                accent: "#ff7043",
                Icon: IconDistortionGrid,
            },
        ],
    },
    {
        id: "grids-cells",
        label: "Grids & Cells",
        tools: [
            { id: "grid-editor", label: "Grid Editor", accent: "#90a4ae", Icon: IconGridEditor },
            { id: "nested-grid", label: "Nested Grid", accent: "#6c8cff", Icon: IconNestedGrid },
            {
                id: "polar-grid-cells",
                label: "Polar Grid Cells",
                accent: "#5bb8a8",
                Icon: IconPolarGridCells,
            },
            {
                id: "voronoi-diagram",
                label: "Voronoi Diagram",
                accent: "#26c6da",
                Icon: IconVoronoi,
            },
            {
                id: "grid-cubes-3d",
                label: "Grid Cubes 3D",
                accent: "#7ca7ff",
                Icon: IconGridCubes3d,
            },
            {
                id: "silhouette-extractor",
                label: "Silhouette Extractor",
                accent: "#a0c4ff",
                Icon: IconSilhouetteExtractor,
            },
            {
                id: "volume-builder-3d",
                label: "Volume Builder 3D",
                accent: "#8fbcff",
                Icon: IconVolumeBuilder3d,
                state: "draft",
            },
        ],
    },
    {
        id: "connections",
        label: "Connections",
        tools: [
            {
                id: "arc-connections",
                label: "Arc Connections",
                accent: "#ff8a65",
                Icon: IconArcConnections,
            },
            {
                id: "rect-node-connections",
                label: "Rect Node Connections",
                accent: "#78909c",
                Icon: IconRectNodeConnections,
            },
            {
                id: "trait-connections",
                label: "Trait Connections",
                accent: "#42a5f5",
                Icon: IconTraitConnections,
            },
        ],
    },
    {
        id: "diagrams",
        label: "Diagrams",
        tools: [
            {
                id: "svg-diagram-2d",
                label: "2D SVG Diagram",
                accent: "#5ec8ea",
                Icon: IconSvgDiagram2D,
                state: "draft",
            },
        ],
    },
    {
        id: "utilities",
        label: "Utilities",
        tools: [
            { id: "svg-to-raster", label: "SVG to Raster", accent: "#ffb74d", Icon: IconSvgToRaster },
        ],
    },
];

const PANEL_CONFIG: PanelConfig[] = PANEL_GROUPS.flatMap((g) => g.tools);

/**
 * Deep-linkable panel ids. Drafts are excluded in production builds so a
 * stale `#draft-tool` URL falls back to the default panel on the live site.
 */
const VALID_PANELS: Set<string> = new Set<string>(
    PANEL_CONFIG.filter(isPanelVisible).map((row) => row.id),
);

function panelFromHash(): PanelType {
    const hash = window.location.hash.replace(/^#\/?/, "");
    if (VALID_PANELS.has(hash)) return hash as PanelType;
    return "object-along-path";
}

export function getPanelLabel(panel: PanelType): string {
    for (const row of PANEL_CONFIG) {
        if (row.id === panel) return row.label;
    }
    return panel;
}

const SIDEBAR_STORAGE_KEY = "panel-sidebar-expanded";

/** Matches `App.scss` mobile layout breakpoint */
const MOBILE_LAYOUT_MEDIA = "(max-width: 768px)";

function readSidebarExpanded(): boolean {
    try {
        const v = localStorage.getItem(SIDEBAR_STORAGE_KEY);
        if (v === null) return true;
        return v === "1";
    } catch {
        return true;
    }
}

interface PanelSelectorProps {
    children: (activePanel: PanelType) => React.ReactNode;
}

export function PanelSelector({ children }: PanelSelectorProps) {
    const [activePanel, setActivePanel] = useState<PanelType>(panelFromHash);
    const [sidebarExpanded, setSidebarExpanded] = useState(readSidebarExpanded);
    const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
    const isMobileLayout = useMediaQuery(MOBILE_LAYOUT_MEDIA);

    const toggleSidebar = useCallback(() => {
        setSidebarExpanded((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? "1" : "0");
            } catch {
                /* ignore */
            }
            return next;
        });
    }, []);

    const navigate = useCallback((panel: PanelType) => {
        setActivePanel(panel);
        window.location.hash = `#${panel}`;
    }, []);

    const navigateFromMobile = useCallback(
        (panel: PanelType) => {
            navigate(panel);
            setMobileToolsOpen(false);
        },
        [navigate],
    );

    useEffect(() => {
        const onHashChange = () => setActivePanel(panelFromHash());
        window.addEventListener("hashchange", onHashChange);
        return () => window.removeEventListener("hashchange", onHashChange);
    }, []);

    useEffect(() => {
        if (!isMobileLayout) setMobileToolsOpen(false);
    }, [isMobileLayout]);

    useEffect(() => {
        if (!mobileToolsOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setMobileToolsOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [mobileToolsOpen]);

    useEffect(() => {
        if (!isMobileLayout || !mobileToolsOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [isMobileLayout, mobileToolsOpen]);

    const rootClass = [
        "panel-selector",
        sidebarExpanded ? "" : "panel-selector--collapsed",
        isMobileLayout ? "panel-selector--mobile-layout" : "",
    ]
        .filter(Boolean)
        .join(" ");

    const visibleGroups = PANEL_GROUPS.map((group) => ({
        ...group,
        tools: group.tools.filter(isPanelVisible),
    })).filter((group) => group.tools.length > 0);

    const toolsNav = (
        <nav className="panel-tabs" aria-label="Tools">
            {visibleGroups.map((group, groupIdx) => (
                <div
                    key={group.id}
                    className={`panel-tab-group ${groupIdx === 0 ? "panel-tab-group--first" : ""}`}
                    role="group"
                    aria-label={group.label}
                >
                    <div className="panel-tab-group-header" aria-hidden>
                        <span className="panel-tab-group-label">
                            {group.label}
                        </span>
                    </div>
                    {group.tools.map(({ id, label, accent, Icon }) => (
                        <button
                            key={id}
                            type="button"
                            className={`panel-tab ${activePanel === id ? "active" : ""}`}
                            style={
                                {
                                    "--panel-tab-accent": accent,
                                } as CSSProperties
                            }
                            onClick={() =>
                                isMobileLayout
                                    ? navigateFromMobile(id)
                                    : navigate(id)
                            }
                            title={label}
                        >
                            <Icon className="panel-tab-icon" />
                            <span className="panel-tab-label">{label}</span>
                        </button>
                    ))}
                </div>
            ))}
        </nav>
    );

    return (
        <div className={rootClass}>
            {!isMobileLayout && (
                <div className="panel-sidebar">
                    <button
                        type="button"
                        className="panel-sidebar-toggle"
                        onClick={toggleSidebar}
                        aria-expanded={sidebarExpanded}
                        title={
                            sidebarExpanded
                                ? "Collapse tool list"
                                : "Expand tool list"
                        }
                    >
                        <svg
                            className="panel-sidebar-toggle-chevron"
                            viewBox="0 0 24 24"
                            width="18"
                            height="18"
                            aria-hidden
                        >
                            {sidebarExpanded ? (
                                <path
                                    d="M14 6l-6 6 6 6"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            ) : (
                                <path
                                    d="M10 6l6 6-6 6"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            )}
                        </svg>
                        {sidebarExpanded && (
                            <span className="panel-sidebar-toggle-text">
                                Tools
                            </span>
                        )}
                    </button>
                    {toolsNav}
                </div>
            )}

            {isMobileLayout && (
                <>
                    <button
                        type="button"
                        className="panel-mobile-tools-btn"
                        onClick={() => setMobileToolsOpen(true)}
                        aria-expanded={mobileToolsOpen}
                        aria-haspopup="dialog"
                        aria-controls="panel-mobile-tools-dialog"
                        title="Tools"
                    >
                        <svg
                            className="panel-mobile-tools-btn-icon"
                            viewBox="0 0 24 24"
                            width="22"
                            height="22"
                            aria-hidden
                        >
                            <path
                                d="M4 6h16M4 12h16M4 18h16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                        <span className="panel-mobile-tools-btn-text">
                            Tools
                        </span>
                    </button>
                    {mobileToolsOpen && (
                        <>
                            <div
                                className="panel-mobile-backdrop"
                                aria-hidden
                                onClick={() => setMobileToolsOpen(false)}
                            />
                            <div
                                id="panel-mobile-tools-dialog"
                                className="panel-mobile-popover"
                                role="dialog"
                                aria-modal="true"
                                aria-label="Tools"
                            >
                                <div className="panel-mobile-popover-header">
                                    <span className="panel-mobile-popover-title">
                                        Tools
                                    </span>
                                    <button
                                        type="button"
                                        className="panel-mobile-popover-close"
                                        onClick={() =>
                                            setMobileToolsOpen(false)
                                        }
                                        aria-label="Close tools menu"
                                    >
                                        x
                                    </button>
                                </div>
                                <div className="panel-mobile-popover-body">
                                    {toolsNav}
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}

            <div className="panel-content">{children(activePanel)}</div>
        </div>
    );
}
